'use client';

import { isLoggedInSelector, useAuthStore } from '@/features/auth/store/auth';
import { useCartStore } from '@/features/cart/store/cart';
import pdf from '@/services/pdf';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FileDownloadOutlined } from '@mui/icons-material';

import CartItemCard from '../CartItemCard';

const CartSummaryPanel = () => {
  const [isLoading, setIsLoading] = useState(false);
  const cartProducts = useCartStore((s) => s.cartProducts);
  const isLoggedIn = useAuthStore(isLoggedInSelector);

  const itemsWithoutDocs = useMemo(() => cartProducts.filter((product) => !product.documentation), [cartProducts]);

  const downloadBrochures = async () => {
    setIsLoading(true);
    const ids = cartProducts.map(({ idProduct }) => idProduct);
    try {
      await pdf.downloadPDFAsync('/pdf/from-repository', 'brochures', ids);
      toast.success('PDF downloaded successfully');
    } finally {
      setIsLoading(false);
      if (itemsWithoutDocs.length) {
        toast.warn(`The products do not have documentation: ${itemsWithoutDocs.map((i) => i.product).join(', ')}`);
      }
    }
  };

  const isExportDisabled = itemsWithoutDocs.length === cartProducts.length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexFlow: 'column' }}>
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', p: 4 }}>
        <Typography variant="cardHeading">Summary</Typography>
      </Box>
      <Box sx={{ overflowY: 'auto', mt: 4, flex: 1, minHeight: 0 }}>
        {cartProducts.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {cartProducts.map((product) => (
              <CartItemCard key={`cart-item-${product.idProduct}`} cartProduct={product} />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Typography>Your cart is empty</Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: 4,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography>
          {cartProducts.length} {cartProducts.length === 1 ? 'solution' : 'solutions'} selected
        </Typography>
        {isLoggedIn && (
          <Tooltip title={isExportDisabled ? 'The selected products do not have any documentation.' : ''}>
            <span>
              <Button
                disabled={isLoading || isExportDisabled || cartProducts.length === 0}
                startIcon={<FileDownloadOutlined />}
                onClick={downloadBrochures}
                variant="outlined"
              >
                Export brochures
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default CartSummaryPanel;
